import React, { useEffect, useState } from 'react';

import DefaultApiLimit from '../../../config/defaultLimit';
import {
  FilterDynamicPriceInterface,
  PricingList,
} from '../../../interface/dynamic-pricing.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchAllDynamicPrice,
  setOffPeakDynamicFilterData,
} from '../../../redux/slice/DynamicOffPeakPriceManagementSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import CustomDateRange from '../../../shared-components/custom-date-range/CustomDateRange';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ExportButton from '../../../shared-components/export-button/ExportButton';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import dynamicPrizeApi from '../../../utils/api/dynamic-prize';

/**
 * value
 * @returns
 */
function OffPeakRateList() {
  const dispatch = useAppDispatch();
  const { pricing, page, limit, totalPages, totalResults, loading, filter } = useAppSelector(
    (states) => states.dynamicOffPeakPrice,
  );
  const [report, setReport] = useState<FilterDynamicPriceInterface>(filter);
  const [exportLoader, setExportLoader] = useState<boolean>(false);

  const reportsColumn = [
    {
      label: 'S. No',
      accessorKey: '' as keyof PricingList,
      /**
       * Renders a custom cell for a pricing list table row.
       * @param {PricingList} v - The pricing list item data.
       * @param {number} index - The index of the current item in the pricing list.
       * @returns {JSX.Element} The rendered JSX element for the table cell.
       */
      customData: (v: PricingList, index: number) => (
        <div className="table-row-each-data">{index + 1}</div>
      ),
      columnWidth: 40,
    },
    {
      label: 'Station Name',
      accessorKey: 'chargingStationName' as keyof PricingList,
      sortAllow: true,
    },
    {
      label: 'Date',
      accessorKey: 'date' as keyof PricingList,
      sortAllow: true,
    },
    // {
    //   label: 'Time',
    //   accessorKey: 'time' as keyof PricingList,
    //   sortAllow: true,
    // },
    {
      label: 'Off Peak Rate',
      accessorKey: 'price' as keyof PricingList,
      sortAllow: true,
    },
    {
      label: 'Revenue',
      accessorKey: 'totalRevenue' as keyof PricingList,
    },
    // {
    //   label: 'User',
    //   accessorKey: 'user' as keyof PricingList,
    //   /**
    //    * rue
    //    * @param v
    //    * @param index
    //    * @returns
    //    */
    // },
  ];

  /**
   * Handle the fetch a pricing data use api function
   * @param data
   */
  const fetchData = (data: { page: number; limit: number; pricingType: string }) => {
    if (!loading) {
      dispatch(fetchAllDynamicPrice(data));
    }
  };

  /**
   * Handle the value change function
   * @param name
   * @param changeValue
   */
  const onChangeValue = (name: string, changeValue: string | number) => {
    setReport((previous: any) => ({
      ...previous,
      [name]: changeValue,
    }));
  };

  /**
   * Handles the change of the current page number.
   * @param {number} pageNumber - The new page number.
   * @returns {void}
   */
  const handlePageChange = (pageNumber: number) => {
    fetchData({
      page: pageNumber,
      limit,
      pricingType: 'offpeak',
    });
  };
  /**
   * Handles the change of the items limit per page.
   * @param {number} changeLimit - The new limit of items per page.
   * @returns {void}
   */
  const handleLimitChange = (changeLimit: number) => {
    fetchData({
      page: DefaultApiLimit.page,
      limit: changeLimit,
      pricingType: 'offpeak',
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    dispatch(
      setOffPeakDynamicFilterData({
        ...sortData,
      }),
    );
    fetchData({
      ...sortData,
      page: DefaultApiLimit.page,
      limit,
      pricingType: 'offpeak',
    });
  };

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    dynamicPrizeApi
      .fetchDynamicPrizeExport({ ...filter, pricingType: 'offpeak' })
      .then((response) => {
        // Convert data to CSV format
        const headers = ['Station Name', 'Date', 'Base Rate', 'Revenue'];
        const rows = response.data.results.map((row: PricingList) => [
          row.chargingStationName || '',
          row.createdAt ? ConvertUTCtoDate(row.createdAt) : '',
          row.price ? Number(row.price).toFixed(2) : '0',
          row.totalRevenue ? Number(row?.totalRevenue).toFixed(2) : '0',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'offpeak-price-list.csv');
          } else {
            exportPDF(headers, rows, 'offpeak-price-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  useEffect(() => {
    if ((report.startDate && report.endDate) || pricing.length === 0) {
      if (report.startDate && report.endDate) {
        dispatch(setOffPeakDynamicFilterData(report));
      }
      fetchData({
        ...report,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
        pricingType: 'offpeak',
      });
    }
  }, [report]);
  return (
    <CustomTable
      header={reportsColumn}
      data={pricing}
      paginate
      loading={loading}
      limit={limit}
      currentPage={page}
      totalPages={totalPages}
      totalResults={totalResults}
      handleSortChange={handleSortFunction}
      handlePageChange={handlePageChange}
      filterAllow={false}
      handleLimitChange={handleLimitChange}
      tableHeight="70vh"
      leftTopContent={
        <div className="reports-custom-date">
          <CustomDateRange
            inputLabel=""
            icon
            name1="startDate"
            name2="endDate"
            placeholder1="From"
            placeholder2="To"
            value1={report.startDate || ''}
            value2={report.endDate || ''}
            onChangeValue={onChangeValue}
          />
        </div>
      }
      footerLeft={
        <div className="table-bottom-button-list">
          <div className="table-button-item">
            <ExportButton
              label="Export Data"
              buttonList={[
                {
                  key: 'pdf',
                  allow: true,
                  /**
                   * Export pdf function
                   * @returns
                   */
                  onClickFunction: () => exportDataPdfOrCSV('pdf'),
                },
                {
                  key: 'csv',
                  allow: true,
                  /**
                   * Export csv function
                   * @returns
                   */
                  onClickFunction: () => exportDataPdfOrCSV('csv'),
                },
              ]}
              disabled={pricing.length === 0}
              loader={exportLoader}
            />
          </div>
        </div>
      }
    />
  );
}

export default OffPeakRateList;
